import React from 'react';
import { Chip } from '@mui/material';
import { ProjectStatus } from '../../models/Project';
import { zoullooColors } from '../../theme';
import { useEnumTranslation } from '../../utils/translateEnum';

interface ProjectStatusBadgeProps {
  status: ProjectStatus;
  size?: 'small' | 'medium';
}

const ProjectStatusBadge: React.FC<ProjectStatusBadgeProps> = ({ status, size = 'small' }) => {
  const { translateProjectStatus } = useEnumTranslation();

  // Define colors for different statuses
  const getColorForStatus = (status: ProjectStatus) => {
    switch (status) {
      case ProjectStatus.Draft:
        return {
          backgroundColor: `${zoullooColors.grey[400]}20`,
          color: zoullooColors.grey[700],
          borderColor: zoullooColors.grey[400],
        };
      case ProjectStatus.Submitted:
        return {
          backgroundColor: `${zoullooColors.info.main}20`,
          color: zoullooColors.info.dark,
          borderColor: zoullooColors.info.main,
        };
      case ProjectStatus.Recruitment:
        return {
          backgroundColor: `${zoullooColors.warning.main}20`,
          color: zoullooColors.warning.dark,
          borderColor: zoullooColors.warning.main,
        };
      case ProjectStatus.Identified:
        return {
          backgroundColor: `${zoullooColors.primary.main}20`,
          color: zoullooColors.primary.dark,
          borderColor: zoullooColors.primary.main,
        };
      case ProjectStatus.Proposed:
        return {
          backgroundColor: `${zoullooColors.secondary.main}20`,
          color: zoullooColors.secondary.dark,
          borderColor: zoullooColors.secondary.main,
        };
      case ProjectStatus.InProgress:
        return {
          backgroundColor: `${zoullooColors.success.main}20`,
          color: zoullooColors.success.dark,
          borderColor: zoullooColors.success.main,
        };
      case ProjectStatus.Delivered:
        return {
          backgroundColor: `${zoullooColors.success.main}30`,
          color: zoullooColors.success.dark,
          borderColor: zoullooColors.success.main,
        };
      case ProjectStatus.Completed:
        return {
          backgroundColor: `${zoullooColors.success.main}40`,
          color: zoullooColors.success.dark,
          borderColor: zoullooColors.success.main,
        };
      case ProjectStatus.Closed:
        return {
          backgroundColor: `${zoullooColors.error.main}20`,
          color: zoullooColors.error.dark,
          borderColor: zoullooColors.error.main,
        };
      case ProjectStatus.Archived:
        return {
          backgroundColor: `${zoullooColors.grey[600]}20`,
          color: zoullooColors.grey[800],
          borderColor: zoullooColors.grey[600],
        };
      default:
        return {
          backgroundColor: `${zoullooColors.grey[500]}20`,
          color: zoullooColors.grey[700],
          borderColor: zoullooColors.grey[500],
        };
    }
  };

  const colors = getColorForStatus(status);

  return (
    <Chip
      label={translateProjectStatus(status)}
      size={size}
      sx={{
        ...colors,
        fontWeight: 500,
        fontSize: size === 'small' ? '0.75rem' : '0.875rem',
        border: `1px solid ${colors.borderColor}`,
        borderRadius: '6px',
      }}
      variant="outlined"
    />
  );
};

export default ProjectStatusBadge;